<script setup lang="ts">
import { ComputedRef, computed, reactive, useSlots } from "vue";

import { groupBy } from "@/utils";
import { MedicalCondition, PrescribedConditionTypes } from "@/entities/conditions";
import Modal from "@/components/Modal.vue";
import { sanitizeString } from "@/utils/string";

export interface Props {
    conditions: MedicalCondition[],
    conditionToEdit: MedicalCondition | null,
}

const props = defineProps<Props>();

const emit = defineEmits<{
        (on:"edit:condition", value: MedicalCondition): void
        (on:"cancel:edit:condition"): void
        (on:"delete:condition", value: MedicalCondition): void
        (on:"save:condition"): void
    }>();

const state = reactive<{
    conditionToDelete: MedicalCondition | null,
    isConditionCancelling: boolean,
    conditionToCancelEdit: MedicalCondition | null,
}>({
    conditionToDelete: null,
    isConditionCancelling: false,
    conditionToCancelEdit: null,
});

const slots = useSlots();

const handleEdit = (condition: MedicalCondition) => {
    emit("edit:condition", condition);
};

const handleCancelEdit = (condition: MedicalCondition) => {
    state.isConditionCancelling = true;
    state.conditionToCancelEdit = condition;
};

const handleDelete = () => {
    if (state.conditionToDelete !== null) {
        emit("delete:condition", state.conditionToDelete);
        state.conditionToDelete = null;
    }
};

const groupedConditions: ComputedRef<Record<PrescribedConditionTypes, MedicalCondition[]>> = computed(() => {
    if (props.conditions) {
        return groupBy(props.conditions, (x) => x.type);
    }
    return {} as Record<PrescribedConditionTypes, MedicalCondition[]>;
});

</script>
<template>
    <div
        v-for="(group, key) in groupedConditions"
        :id="`${group[0].type}Group`"
        :key="key"
        class="border border-info bg-white rounded p-3 medical-frame"
    >
        <div
            class="row"
        >
            <div
                class="col-12 col-md-6 col-lg-8 font-weight-bold"
            >
                <span>{{ group[0].type }} conditions</span>
            </div>
            <div
                v-if="'subConditions' in group[0]"
                class="col-12 col-md-3 col-lg-2 py-2 py-md-0"
            >
                <button
                    data-testid="editCondition"
                    type="button"
                    class="btn btn-primary btn-block font-weight-bold w-100 mb-2 mb-md-0 w-md-auto"
                    @click="group[0].id === props.conditionToEdit?.id ? handleCancelEdit(group[0]) : handleEdit(group[0])"
                >
                    {{ group[0].id === props.conditionToEdit?.id ? 'Cancel' : 'Edit' }}
                </button>
            </div>
            <div
                v-if="'subConditions' in group[0]"
                class="col-12 col-md-3 col-lg-2"
            >
                <button
                    data-testid="deleteCondition"
                    type="button"
                    class="btn btn-conditions btn-block btn-danger font-weight-bold w-100 w-md-auto"
                    @click="state.conditionToDelete = group[0]"
                >
                    Remove
                </button>
            </div>
        </div>
        <hr class="hrDot">
        <div
            v-if="'subConditions' in group[0]"
        >
            <ul
                v-if="!slots[sanitizeString(group[0].name)]"
                class="list-group list-group-flush"
            >
                <li
                    v-for="(condition) in group[0].subConditions"
                    :id="`conditions${sanitizeString(condition.name)}`"
                    :key="condition.name"
                    class="list-group-item px-0"
                    :class="`${group[0].type}Condition`"
                >
                    <span class="col-md-8 align-items-center d-flex">{{ condition.name }}</span>
                </li>
            </ul>
            <slot :name="sanitizeString(group[0].name)" />
        </div>
        <ul
            v-else
            class="list-group list-group-flush p-0 m-0"
        >
            <li
                v-for="(condition) in group"
                :id="`conditions${sanitizeString(condition.name)}`"
                :key="condition.name"
                class="list-group-item px-0"
                :class="`${(group as MedicalCondition[])[0].type}Condition`"
            >
                <div class="row">
                    <span class="col-12 col-md-6 col-lg-8 align-items-center d-flex">{{ condition.name }}</span>
                    <div class="col-12 col-md-3 col-lg-2">
                        <button
                            data-testid="editCondition"
                            type="button"
                            class="btn btn-primary btn-block font-weight-bold my-2 my-md-0 w-100 w-md-auto"
                            @click="condition.id === props.conditionToEdit?.id ? handleCancelEdit(group[0]) : handleEdit(condition)"
                        >
                            {{ condition.id === props.conditionToEdit?.id ? 'Cancel' : 'Edit' }}
                        </button>
                    </div>
                    <div class="col-12 col-md-3 col-lg-2">
                        <button
                            data-testid="deleteCondition"
                            type="button"
                            class="btn btn-conditions btn-block btn-danger font-weight-bold w-100 w-md-auto mb-1"
                            @click="state.conditionToDelete = condition"
                        >
                            Remove
                        </button>
                    </div>
                    <slot :name="sanitizeString(condition.name)" />
                </div>
            </li>
        </ul>
        <Modal
            id="condition-cancel-modal"
            header="Before you close"
            open-focus="close-condition-cancel-modal"
            :show="state.isConditionCancelling"
            @close="state.isConditionCancelling = false"
        >
            <template #body>
                <p>Do you want to save your changes or close without saving</p>
            </template>
            <template #footer>
                <button
                    :id="`cancelCondition_${sanitizeString(state.conditionToCancelEdit?.name)}`"
                    type="button"
                    class="btn btn-danger font-weight-bold mx-auto mx-sm-0"
                    @click.prevent="state.isConditionCancelling = false; emit('cancel:edit:condition')"
                >
                    Close and lose unsaved changes
                </button>
                <button
                    :id="`confirmCondition_${sanitizeString(state.conditionToCancelEdit?.name)}`"
                    type="button"
                    class="btn btn-primary font-weight-bold mx-auto mx-sm-0"
                    @click.prevent="state.isConditionCancelling = false; emit('save:condition')"
                >
                    Save changes
                </button>
            </template>
        </Modal>
        <Modal
            id="condition-delete-modal"
            open-focus="close-condition-delete-modal"
            :header="`Are you sure? (Remove ${state.conditionToDelete?.name})`"
            :show="state.conditionToDelete !== null"
            @close="state.conditionToDelete = null"
        >
            <template #body>
                <p>Removing this condition will be permanent and cant be undone.</p>
            </template>
            <template #footer>
                <button
                    :id="`cancelRemovingCondition_${state.conditionToDelete?.id}`"
                    type="button"
                    class="btn btn-secondary"
                    @click.prevent="state.conditionToDelete = null"
                >
                    Cancel
                </button>
                <button
                    :id="`confirmRemovingCondition_${state.conditionToDelete?.id}`"
                    type="button"
                    class="btn btn-danger font-weight-bold"
                    @click.prevent="handleDelete"
                >
                    Remove
                </button>
            </template>
        </Modal>
    </div>
    <slot />
</template>
